import { useTranslation } from 'react-i18next';
import {
  EPIKRISE_CONTACT_MAX,
  EPIKRISE_DIAGNOSE_CODE_MAX,
  EPIKRISE_DIAGNOSE_TEXT_MAX,
  EPIKRISE_MAIN_ADDITIONAL_MAX,
  EPIKRISE_OTHER_MAX,
  EPIKRISE_RECOMMENDATION_MAX,
  EPIKRISE_RELATIVES_MAX,
  EPIKRISE_TEXT01_LIMIT,
  type EpikriseIssue,
  type EpikriseKind,
} from '@/lib/referral/epikrise';
import { FORM_ERROR_TYPE } from '@/lib/typography';

export type EpikriseBlocker =
  | EpikriseIssue
  | 'receiver_missing'
  | 'receiver_incomplete'
  | 'cc_incomplete'
  | 'attachments_invalid'
  | 'signer_loading';

/** Every reason 'Dan epikrise' was refused, listed by the submit button (never a silent block). */
export function EpikriseIssueSummary({ kind, blockers }: { kind: EpikriseKind; blockers: EpikriseBlocker[] }) {
  const { t } = useTranslation();
  if (blockers.length === 0) return null;
  const message = (b: EpikriseBlocker): string => {
    switch (b) {
      case 'main_diagnosis':
        return t('referrals.epikrise_main_diagnosis_missing', 'Vælg én aktionsdiagnose.');
      case 'main_additional_max':
        return t('referrals.epikrise_additional_max', 'Der kan højst vælges {{max}} aktionstillægsdiagnoser.', { max: EPIKRISE_MAIN_ADDITIONAL_MAX });
      case 'recommendation_missing':
        return t(
          'referrals.epikrise_recommendation_missing',
          'Advarsel: Der er valgt en markering. Udfyld "Anbefaling om opfølgning hos egen læge", før epikrisen kan dannes.',
        );
      case 'recommendation_too_long':
        return t('referrals.epikrise_recommendation_too_long', 'Anbefalingen må højst være {{max}} tegn.', { max: EPIKRISE_RECOMMENDATION_MAX });
      case 'recommendation_markup':
      case 'text_markup':
        return t('referrals.epikrise_markup_problem', 'Fremhævning kan ikke indlejres, og hver [b], [i] og [u] skal afsluttes.');
      case 'text_too_long':
        return t('referrals.epikrise_text_too_long', 'Epikriseteksten må højst være {{max}} tegn.', { max: EPIKRISE_TEXT01_LIMIT[kind] });
      case 'contact_too_long':
        return t('referrals.epikrise_contact_too_long', 'Kontaktpersonens titel og navn må højst være {{max}} tegn hver.', {
          max: EPIKRISE_CONTACT_MAX,
        });
      case 'relatives_max':
        return t('referrals.epikrise_relatives_max', 'Der kan højst angives {{max}} pårørende.', { max: EPIKRISE_RELATIVES_MAX });
      case 'relative_surname':
        return t('referrals.epikrise_relative_surname_missing', 'Angiv efternavn på pårørende.');
      case 'end_before_start':
        return t('referrals.epikrise_end_before_start', 'Slutdatoen ligger før startdatoen.');
      case 'dates':
        return t('referrals.epikrise_dates_invalid', 'Datoerne skal være ÅÅÅÅ-MM-DD.');
      case 'receiver_missing':
      case 'receiver_incomplete':
        return t(
          'referrals.epikrise_receiver_missing',
          'Vælg en modtager med SOR-identifikator (ydernummer eller sygehusafdelingsnummer).',
        );
      case 'cc_incomplete':
        return t('referrals.epikrise_cc_incomplete', 'Kopimodtageren mangler SOR-identifikator.');
      case 'other_max':
        return t('referrals.epikrise_other_max', 'Der kan højst angives {{max}} yderligere diagnoser.', { max: EPIKRISE_OTHER_MAX });
      case 'other_incomplete':
        return t('referrals.epikrise_other_incomplete', 'Angiv både kode og tekst for hver yderligere diagnose, eller fjern rækken.');
      case 'other_too_long':
        return t('referrals.epikrise_other_too_long', 'Koden må højst være {{code}} tegn og teksten højst {{text}} tegn.', {
          code: EPIKRISE_DIAGNOSE_CODE_MAX,
          text: EPIKRISE_DIAGNOSE_TEXT_MAX,
        });
      case 'other_time_without_date':
        return t('referrals.epikrise_other_time_without_date', 'Et klokkeslæt kræver en dato.');
      case 'signer_missing':
        return t('referrals.epikrise_signer_missing', 'Din bruger har intet navn. Angiv dit navn i din profil, før epikrisen dannes.');
      case 'signer_loading':
        return t('referrals.epikrise_signer_loading_blocker', 'Dit navn og din titel er ikke hentet endnu. Prøv igen om lidt.');
      case 'attachments_invalid':
        return t('referrals.epikrise_attachments_invalid', 'Et eller flere bilag kan ikke sendes. Se begrundelsen ved bilaget.');
    }
  };
  const messages = [...new Set(blockers.map(message))];
  return (
    <div role="alert" className={FORM_ERROR_TYPE} data-testid="epikrise-issues">
      <p>{t('referrals.epikrise_invalid', 'Epikrisen er ikke dannet. Ret følgende:')}</p>
      <ul className="ml-4 list-disc">
        {messages.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </div>
  );
}
